import client from './client';
import type {
  Event,
  EventAssignment,
  EventType,
  MemberUnavailability,
} from '../types/event';

const base = (teamId: string) => `/teams/${teamId}`;

// ---------------------------------------------------------------------------
// EventType
// ---------------------------------------------------------------------------

export async function listEventTypes(teamId: string): Promise<EventType[]> {
  const res = await client.get(`${base(teamId)}/event-types/`);
  return res.data;
}

export async function createEventType(
  teamId: string,
  payload: {
    name: string;
    recurrence_pattern: string;
    default_day_of_week?: number | null;
    default_time?: string | null;
    roles?: { instrument_id: string; quantity: number }[];
  }
): Promise<EventType> {
  const res = await client.post(`${base(teamId)}/event-types/`, payload);
  return res.data;
}

export async function updateEventType(
  teamId: string,
  typeId: string,
  payload: Partial<{
    name: string;
    recurrence_pattern: string;
    default_day_of_week: number | null;
    default_time: string | null;
    roles: { instrument_id: string; quantity: number }[];
  }>
): Promise<EventType> {
  const res = await client.patch(`${base(teamId)}/event-types/${typeId}/`, payload);
  return res.data;
}

export async function deleteEventType(teamId: string, typeId: string): Promise<void> {
  await client.delete(`${base(teamId)}/event-types/${typeId}/`);
}

export async function generateEventsFromType(
  teamId: string,
  typeId: string,
  startDate: string,
  endDate: string
): Promise<{ created: number; events: Event[] }> {
  const res = await client.post(`${base(teamId)}/event-types/${typeId}/generate/`, {
    start_date: startDate,
    end_date: endDate,
  });
  return res.data;
}

// ---------------------------------------------------------------------------
// Event
// ---------------------------------------------------------------------------

export interface EventFilters {
  event_type_id?: string;
  status?: string;
  date_from?: string;
  date_to?: string;
}

export async function listEvents(teamId: string, filters?: EventFilters): Promise<Event[]> {
  const res = await client.get(`${base(teamId)}/events/`, { params: filters });
  return res.data;
}

export async function createEvent(
  teamId: string,
  payload: {
    name: string;
    date: string;
    event_type_id?: string | null;
    start_time?: string | null;
    end_time?: string | null;
    setlist_id?: string | null;
    notes?: string;
  }
): Promise<Event> {
  const res = await client.post(`${base(teamId)}/events/`, payload);
  return res.data;
}

export async function updateEvent(
  teamId: string,
  eventId: string,
  payload: Partial<{
    name: string;
    date: string;
    start_time: string | null;
    end_time: string | null;
    setlist_id: string | null;
    notes: string;
    status: string;
  }>
): Promise<Event> {
  const res = await client.patch(`${base(teamId)}/events/${eventId}/`, payload);
  return res.data;
}

export async function cancelEvent(teamId: string, eventId: string): Promise<void> {
  await client.delete(`${base(teamId)}/events/${eventId}/`);
}

// ---------------------------------------------------------------------------
// EventAssignment
// ---------------------------------------------------------------------------

export async function listAssignments(teamId: string, eventId: string): Promise<EventAssignment[]> {
  const res = await client.get(`${base(teamId)}/events/${eventId}/assignments/`);
  return res.data;
}

export async function createAssignment(
  teamId: string,
  eventId: string,
  memberId: string,
  instrumentId: string
): Promise<EventAssignment> {
  const res = await client.post(`${base(teamId)}/events/${eventId}/assignments/`, {
    member_id: memberId,
    instrument_id: instrumentId,
  });
  return res.data;
}

export async function updateAssignmentStatus(
  teamId: string,
  eventId: string,
  assignmentId: string,
  status: 'CONFIRMED' | 'DECLINED',
  declinedReason?: string
): Promise<EventAssignment> {
  const res = await client.patch(
    `${base(teamId)}/events/${eventId}/assignments/${assignmentId}/`,
    { status, declined_reason: declinedReason ?? '' }
  );
  return res.data;
}

export async function deleteAssignment(
  teamId: string,
  eventId: string,
  assignmentId: string
): Promise<void> {
  await client.delete(`${base(teamId)}/events/${eventId}/assignments/${assignmentId}/`);
}

export async function listMyAssignments(
  teamId: string,
  dateFrom?: string,
  dateTo?: string
): Promise<EventAssignment[]> {
  const res = await client.get(`${base(teamId)}/schedule/my-assignments/`, {
    params: { date_from: dateFrom, date_to: dateTo },
  });
  return res.data;
}

// ---------------------------------------------------------------------------
// MemberUnavailability
// ---------------------------------------------------------------------------

export async function listUnavailabilities(teamId: string): Promise<MemberUnavailability[]> {
  const res = await client.get(`${base(teamId)}/unavailabilities/`);
  return res.data;
}

export async function createUnavailability(
  teamId: string,
  startDate: string,
  endDate: string,
  reason?: string
): Promise<MemberUnavailability> {
  const res = await client.post(`${base(teamId)}/unavailabilities/`, {
    start_date: startDate,
    end_date: endDate,
    reason: reason ?? '',
  });
  return res.data;
}

export async function updateUnavailability(
  teamId: string,
  unavailId: string,
  payload: Partial<{ start_date: string; end_date: string; reason: string }>
): Promise<MemberUnavailability> {
  const res = await client.patch(`${base(teamId)}/unavailabilities/${unavailId}/`, payload);
  return res.data;
}

export async function deleteUnavailability(teamId: string, unavailId: string): Promise<void> {
  await client.delete(`${base(teamId)}/unavailabilities/${unavailId}/`);
}
